import { SUBSCRIPTION_TIERS, type SubscriptionTier } from '@/api/subscriptions';
import { PricingCard } from './PricingCard';
import { useCampaignStatus } from '@/hooks/useCampaignStatus';
import { CAMPAIGNS } from '@/api/subscriptions';
import { Tag } from 'lucide-react';

interface PricingGridProps {
  recommendedTierId?: string;
  ctaLink?: string;
  ctaText?: string;
  onTierClick?: (tier: SubscriptionTier) => void;
  useCTAButton?: boolean;
}

export function PricingGrid({
  recommendedTierId = 'pro',
  ctaLink = '/?auth=signup',
  ctaText = 'Start Writing',
  onTierClick,
  useCTAButton = true,
}: PricingGridProps) {
  const { isCampaignActive, getCampaignDiscount } = useCampaignStatus();

  // Check if all tiers have the same global campaign active
  const allTiersHaveGlobalCampaign = SUBSCRIPTION_TIERS.every(tier =>
    tier.campaign &&
    CAMPAIGNS[tier.campaign]?.isGlobal &&
    isCampaignActive(tier.campaign)
  );

  // Get the global campaign details (from first tier since they all have the same one)
  const globalCampaign = allTiersHaveGlobalCampaign && SUBSCRIPTION_TIERS[0].campaign
    ? CAMPAIGNS[SUBSCRIPTION_TIERS[0].campaign]
    : null;

  return (
    <div className="space-y-6">
      {allTiersHaveGlobalCampaign && globalCampaign && (
        <div className="mx-auto bg-gradient-to-r from-blue-500/20 to-cyan-500/20 backdrop-blur-sm border border-blue-400/30 px-6 py-3 rounded-sm flex items-center gap-1.5 w-fit">
          <Tag className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
          <span className="text-blue-700 dark:text-blue-300 font-semibold text-sm">{globalCampaign.description}</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-5xl mx-auto items-stretch">
        {SUBSCRIPTION_TIERS.map((tier) => (
          <PricingCard
            key={tier.id}
            tier={tier}
            isRecommended={tier.id === recommendedTierId}
            ctaLink={ctaLink}
            ctaText={ctaText}
            onCtaClick={onTierClick ? () => onTierClick(tier) : undefined}
            useCTAButton={useCTAButton}
            isCampaignActive={tier.campaign ? isCampaignActive(tier.campaign) : true}
            polarDiscount={tier.campaign ? getCampaignDiscount(tier.campaign) : null}
          />
        ))}
      </div>
    </div>
  );
}
